// Which of the customer's bookings are in the new eSign flow (project launch). GET /allotment-esign/flow
// is fetched once and shared by every screen. Every mounted screen hears about each new result, so a
// component mounted early and kept on screen (the Virtual Account card in the main layout) updates
// when a later fetch finishes. A screen mounting more than STALE_MS after the last fetch, or after a
// failed fetch, fetches again; refreshEsignFlow() forces a fetch (after KYC is submitted).
// Bookings not in the list keep the old flow.
import { useEffect, useState } from "react";
import { getEsignFlow } from "../../Services/AllotmentEsign";
import { portalToken } from "../../Services/ServerOtp";

export const STALE_MS = 60 * 1000;

let cache = null; // { token, at, promise, result: { items } | { error } | undefined }
const listeners = new Set();

function load(force = false) {
	const token = portalToken() || "";
	const fresh = cache && cache.token === token && Date.now() - cache.at < STALE_MS && !cache.result?.error;
	if (!force && fresh) return cache;
	const entry = { token, at: Date.now(), result: undefined };
	entry.promise = getEsignFlow()
		.then((res) => (entry.result = { items: Array.isArray(res?.items) ? res.items : [] }))
		.catch((err) => {
			console.error("allotment eSign flow error:", err);
			return (entry.result = { error: true });
		})
		.then((result) => {
			// only the latest fetch is announced; an older one finishing late is ignored
			if (cache === entry) listeners.forEach((fn) => fn(result));
			return result;
		});
	cache = entry;
	return entry;
}

export const resetEsignFlowCache = () => {
	cache = null;
};

// Fetch again now and update every screen (e.g. after the KYC form is submitted).
export const refreshEsignFlow = () => load(true).promise;

// (result, allotmentPaymentId) → true | false | null (unknown: loading or error)
export function flowLookup(result, allotmentPaymentId) {
	if (!result || result.error) return null;
	if (!allotmentPaymentId) return false;
	return result.items.some((i) => i.allotmentPaymentId === allotmentPaymentId);
}

export const flowKycPending = (result) => !!result?.items?.some((i) => i.kycFormFilled === false);

// { newFlow(allotmentPaymentId) → true | false | null, anyKycPending }
export default function useEsignFlow() {
	const [result, setResult] = useState(() => (cache && cache.token === (portalToken() || "") ? cache.result : undefined));

	useEffect(() => {
		let alive = true;
		const onResult = (r) => alive && setResult(r);
		listeners.add(onResult);
		const entry = load();
		if (entry.result !== undefined) setResult(entry.result);
		return () => {
			alive = false;
			listeners.delete(onResult);
		};
	}, []);

	return {
		newFlow: (allotmentPaymentId) => flowLookup(result, allotmentPaymentId),
		anyKycPending: flowKycPending(result),
	};
}
